/* The second sidebar a page or dialog keeps for its own sections — workspace
   Settings, an agent's Settings. A grouped rail beside the content from `md`
   up; on a phone the same sections as a strip of chips above it. Render it
   first inside a `flex flex-col md:flex-row` box, with the content after it. */
import React, { useRef } from "react";

import type { LucideIcon } from "lucide-react";
import type { AnimatedIconHandle, AnimatedIconProps } from "@/shared/components/icons/animated";

/** An animated icon (its ref starts and stops it) or a plain lucide glyph (its
    ref is the `<svg>`, so the start/stop calls no-op). */
export type SectionNavIcon =
  | React.ForwardRefExoticComponent<AnimatedIconProps & React.RefAttributes<AnimatedIconHandle>>
  | LucideIcon;

export interface SectionNavItem<T extends string> {
  id: T;
  label: string;
  icon: SectionNavIcon;
  /** Shown at the row's end: a count (set in mono), or a word like "Off". */
  meta?: React.ReactNode;
}

export interface SectionNavGroup<T extends string> {
  label?: string;
  items: SectionNavItem<T>[];
}

function NavRow<T extends string>({ item, active, onClick }: {
  item: SectionNavItem<T>;
  active: boolean;
  onClick: () => void;
}) {
  // One ref serves both icon families; the calls are optional for lucide's.
  const iconRef = useRef<AnimatedIconHandle & SVGSVGElement>(null);
  const Icon = item.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      onMouseEnter={() => iconRef.current?.startAnimation?.()}
      onMouseLeave={() => iconRef.current?.stopAnimation?.()}
      className={`press w-full h-[30px] flex items-center gap-2 px-2 rounded-[8px] text-left ${
        active
          ? "bg-[var(--accent-active)] text-[var(--text-primary)] font-medium"
          : "text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]"
      }`}
    >
      <Icon ref={iconRef} size={16} strokeWidth={1.75} className={`shrink-0 ${active ? "text-[var(--text-primary)]" : "text-[var(--text-tertiary)]"}`} />
      <span className="min-w-0 flex-1 text-[13.5px] truncate">{item.label}</span>
      {item.meta !== undefined && (
        <span className={`shrink-0 whitespace-nowrap text-[11.5px] text-[var(--text-quaternary)] ${typeof item.meta === "number" ? "font-mono tabular-nums" : ""}`}>{item.meta}</span>
      )}
    </button>
  );
}

export default function SectionNav<T extends string>({ title, groups, active, onSelect, footer }: {
  /** The rail's heading: a word ("Settings"), or a whole identity row. */
  title: React.ReactNode;
  groups: SectionNavGroup<T>[];
  active: T;
  onSelect: (id: T) => void;
  /** Pinned to the bottom of the rail; the phone strip leaves it out. */
  footer?: React.ReactNode;
}) {
  return (
    <>
      <div className="md:hidden shrink-0 border-b hairline bg-[var(--surface-sunk)] px-3 pt-[max(env(safe-area-inset-top),12px)] pb-2.5">
        <div className="px-1 pb-2 text-[14px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">{title}</div>
        <nav className="flex gap-1.5 overflow-x-auto no-scrollbar scroll-px-3">
          {groups.flatMap((g) => g.items).map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => onSelect(item.id)}
              aria-current={item.id === active ? "page" : undefined}
              className={`press shrink-0 whitespace-nowrap h-7 px-3 rounded-full text-[13px] tracking-[-0.005em] ${
                item.id === active
                  ? "bg-[var(--accent-active)] text-[var(--text-primary)] font-medium"
                  : "text-[var(--text-secondary)] hover:bg-[var(--accent-hover)]"
              }`}
            >
              {item.label}
            </button>
          ))}
        </nav>
      </div>
      <nav className="hidden md:flex w-[216px] shrink-0 flex-col bg-[var(--surface-sunk)] border-r hairline px-2.5 pt-5 overflow-y-auto">
        <div className="px-2 pb-2 text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">{title}</div>
        {groups.map((g, i) => (
          <div key={g.label ?? i}>
            {g.label ? <div className="px-2 pt-3 pb-1 section-label">{g.label}</div> : <div className="pt-3" />}
            <div className="space-y-[1px]">
              {g.items.map((item) => (
                <NavRow key={item.id} item={item} active={item.id === active} onClick={() => onSelect(item.id)} />
              ))}
            </div>
          </div>
        ))}
        <div className="mt-auto pt-4 pb-2.5">{footer}</div>
      </nav>
    </>
  );
}
